import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import { toast } from "@/lib/toast";
import { KeyRound, RefreshCw, Check, X, ArrowRight, ShieldAlert, Loader2 } from "lucide-react";

const STATUS_FILTERS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "denied", label: "Denied" },
  { id: "applied", label: "Applied" },
  { id: "all", label: "All" },
];

const STATUS_CLS = {
  pending: "border-amber-400/50 text-amber-400",
  approved: "border-steel/60 text-steel-bright",
  applied: "border-teal-300/50 text-teal-300",
  denied: "border-sniper text-muted-foreground",
  cancelled: "border-sniper text-muted-foreground",
  expired: "border-sniper text-muted-foreground",
};

export default function AdminContactChanges() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [denyFor, setDenyFor] = useState("");
  const [denyReason, setDenyReason] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/contact-changes?status=${status}`)
      .then(({ data }) => setRows(data.requests || []))
      .catch((err) => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(`Approve this ${r.field} change for @${r.username}?\n\n${r.current_value} → ${r.requested_value}\n\nThe new address must still be confirmed by the user before it takes effect.`)) return;
    setBusy(r.request_id);
    try {
      await api.post(`/admin/contact-changes/${r.request_id}/approve`);
      toast.success("Approved — a confirmation link was sent to the new address.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitDeny = async (r) => {
    if (denyReason.trim().length < 5) { toast.error("Enter a reason of at least 5 characters."); return; }
    setBusy(r.request_id);
    try {
      await api.post(`/admin/contact-changes/${r.request_id}/deny`, { reason: denyReason.trim() });
      toast.success("Request denied — the user was notified.");
      setDenyFor(""); setDenyReason("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="text-foreground" data-testid="admin-contact-changes-page">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
        <KeyRound className="w-7 h-7 text-olive-bright" /> Contact Changes
      </h2>
      <p className="text-sm font-body text-secondary-foreground mt-2">
        Locked-email change requests. A user whose identity and email are verified can't edit their
        email directly — they must request it, an admin approves here, and then the user re-confirms
        the new address before it applies. This blocks silent account takeovers.
      </p>
      <Crosshair className="my-5" />

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="contact-status-filters">
          {STATUS_FILTERS.map((f) => (
            <button key={f.id} onClick={() => setStatus(f.id)} data-testid={`contact-filter-${f.id}`}
              className={`font-mono uppercase text-[11px] px-3 py-1.5 border transition-colors ${status === f.id
                ? "border-olive-bright bg-olive/20 text-foreground"
                : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors" title="Refresh" data-testid="contact-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="border border-sniper bg-surface" data-testid="contact-changes-list">
        {loading ? (
          <p className="p-8 text-center font-mono uppercase text-sm text-muted-foreground inline-flex w-full items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center font-mono uppercase text-sm text-muted-foreground" data-testid="contact-empty">
            No {status === "all" ? "" : status} requests.
          </p>
        ) : (
          <ul className="divide-y divide-sniper">
            {rows.map((r) => (
              <li key={r.request_id} className="px-4 py-4" data-testid={`contact-change-row-${r.request_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-body text-foreground flex items-center gap-2 flex-wrap">
                      <Link to={`/u/${r.username}`} className="font-mono text-foreground hover:text-olive-bright">@{r.username}</Link>
                      <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-sniper text-muted-foreground">{r.field}</span>
                      {r.is_admin && <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-blaze/50 text-blaze inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> admin</span>}
                    </p>
                    <p className="text-sm font-mono mt-1.5 flex items-center gap-2 flex-wrap break-all">
                      <span className="text-muted-foreground line-through">{r.current_value}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-olive-bright shrink-0" />
                      <span className="text-foreground">{r.requested_value}</span>
                    </p>
                    {r.reason && <p className="text-[13px] font-body text-secondary-foreground mt-1.5">“{r.reason}”</p>}
                    <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1.5">
                      Requested {timeAgo(r.created_at)} · {r.listings_count} listings · {r.orders_count} orders · joined {r.account_created_at ? timeAgo(r.account_created_at) : "—"}
                    </p>
                    {r.admin_reason && <p className="text-[11px] font-mono text-blaze mt-1">Denied: {r.admin_reason}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${STATUS_CLS[r.status] || "border-sniper text-muted-foreground"}`}>{r.status}</span>
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => approve(r)} disabled={busy === r.request_id} data-testid={`contact-approve-${r.request_id}`}
                          className="inline-flex items-center gap-1 border border-olive-bright/60 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={() => { setDenyFor(denyFor === r.request_id ? "" : r.request_id); setDenyReason(""); }} disabled={busy === r.request_id} data-testid={`contact-deny-${r.request_id}`}
                          className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 disabled:opacity-50 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                          <X className="w-3 h-3" /> Deny
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {denyFor === r.request_id && (
                  <div className="mt-3 border border-blaze/40 bg-blaze/5 p-3" data-testid={`contact-deny-panel-${r.request_id}`}>
                    <label className="font-mono text-[10px] uppercase text-blaze block mb-1">Reason (shared with the user, min 5 chars)</label>
                    <textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} rows={2} maxLength={500}
                      data-testid={`contact-deny-reason-${r.request_id}`}
                      className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm"
                      placeholder="e.g. Could not confirm this request belongs to you." />
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => submitDeny(r)} disabled={busy === r.request_id || denyReason.trim().length < 5}
                        data-testid={`contact-deny-submit-${r.request_id}`}
                        className="inline-flex items-center gap-1 bg-blaze hover:bg-blaze/90 disabled:opacity-50 text-white font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                        <X className="w-3 h-3" /> Confirm deny
                      </button>
                      <button onClick={() => { setDenyFor(""); setDenyReason(""); }}
                        className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground px-2 py-1.5">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
